import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { FinancialHubComponent } from './financial-hub.component';
import { CompaniesComponent } from './components/companies/companies.component';
import { CompanyDetailComponent } from './components/company-detail/company-detail.component';
import { CompanyFormComponent } from './components/company-form/company-form.component';
import { PurchaseOrdersComponent } from './components/purchase-orders/purchase-orders.component';
import { PurchaseOrderDetailComponent } from './components/purchase-order-detail/purchase-order-detail.component';
import { PurchaseOrderViewComponent } from './components/purchase-order-view/purchase-order-view.component';
import { FinancialHubAccessGuard } from './guards/financial-hub-access.guard';
import { CountryQueryParamGuard } from './guards/country-query-param.guard';

const TITLE_BAR_BASE = {
  title: 'Financial Hub',
  actions: []
};

// The `companies` and `purchase-orders` routes are component-less wrappers so each detail/edit/create
// view is a CHILD of its list rather than a sibling. This makes the list show up as a clickable
// intermediate breadcrumb crumb ("Companies" / "Purchase orders") that returns to the right list
// when clicked. Without the wrapper, the detail's activation chain skips the list route, so the
// only clickable crumb above it is "Financial Hub" — which navigates to `/financial-hub` and hits
// the empty-path redirect to `purchase-orders`, dropping the user on the wrong tab.
const routes: Routes = [
  {
    path: '',
    component: FinancialHubComponent,
    canActivate: [FinancialHubAccessGuard],
    data: { ...TITLE_BAR_BASE, module: 'financial-hub' },
    children: [
      {
        path: '',
        redirectTo: 'purchase-orders',
        pathMatch: 'full'
      },
      {
        path: 'companies',
        // `module` is mirrored here so BackOfficeComponent.getParam still resolves the submodule
        // to `financial-hub/companies` — it descends only while firstChild.data.module is set.
        //
        // `title` must be set here too, even though this route has no component of its own:
        // OneTitleBarService.getRouteData() (@rappi/ui/title-bar) walks root -> leaf and, at every
        // level whose immediate child has no `title`, captures the PARENT's title as a fallback —
        // then unconditionally lets that captured value win over the leaf's own title in the final
        // merge. Without a `title` here, navigating to `create`/`:id/edit` captured the root's
        // 'Financial Hub' at this hop and clobbered their own 'New company' / 'Edit company'.
        data: {
          title: 'Companies',
          breadcrumbs: 'Companies',
          breadcrumbsLink: true,
          module: 'financial-hub/companies'
        },
        children: [
          {
            path: '',
            component: CompaniesComponent,
            data: {
              ...TITLE_BAR_BASE,
              module: 'financial-hub/companies'
            }
          },
          {
            path: 'create',
            component: CompanyFormComponent,
            data: {
              ...TITLE_BAR_BASE,
              title: 'New company',
              breadcrumbs: 'New company',
              // Not hideModuleTitle: true — that flag makes BackOfficeComponent skip rendering
              // <app-title-section> entirely (back-office.component.html), which is the ONLY
              // place `title` is displayed. With it set, "New company" existed in the route data
              // but never appeared anywhere on screen.
              module: 'financial-hub/companies',
              mode: 'create'
            }
          },
          {
            // Must precede the :id route or Angular matches :id first and loads the detail view.
            path: ':id/edit',
            component: CompanyFormComponent,
            canActivate: [CountryQueryParamGuard],
            data: {
              ...TITLE_BAR_BASE,
              title: 'Edit company',
              breadcrumbs: 'Edit company',
              // See the `create` route above for why hideModuleTitle is intentionally absent.
              module: 'financial-hub/companies',
              mode: 'edit'
            }
          },
          {
            path: ':id',
            component: CompanyDetailComponent,
            canActivate: [CountryQueryParamGuard],
            data: {
              ...TITLE_BAR_BASE,
              breadcrumbs: 'Company detail',
              hideModuleTitle: true,
              module: 'financial-hub/companies'
            }
          }
        ]
      },
      {
        path: 'purchase-orders',
        // See the `companies` wrapper above for why `module` is mirrored here and why `title`
        // must be set even without a component of its own.
        data: {
          title: 'Purchase orders',
          breadcrumbs: 'Purchase orders',
          breadcrumbsLink: true,
          module: 'financial-hub/purchase-orders'
        },
        children: [
          {
            path: '',
            component: PurchaseOrdersComponent,
            canActivate: [CountryQueryParamGuard],
            data: {
              ...TITLE_BAR_BASE,
              module: 'financial-hub/purchase-orders'
            }
          },
          {
            path: 'create',
            component: PurchaseOrderDetailComponent,
            data: {
              ...TITLE_BAR_BASE,
              title: 'New purchase order',
              breadcrumbs: 'New purchase order',
              module: 'financial-hub/purchase-orders',
              mode: 'create'
            }
          },
          {
            // Must precede the :id route or Angular matches :id first and loads the view component.
            path: ':id/edit',
            component: PurchaseOrderDetailComponent,
            canActivate: [CountryQueryParamGuard],
            data: {
              ...TITLE_BAR_BASE,
              title: 'Edit purchase order',
              breadcrumbs: 'Edit purchase order',
              module: 'financial-hub/purchase-orders',
              mode: 'edit'
            }
          },
          {
            path: ':id',
            component: PurchaseOrderViewComponent,
            canActivate: [CountryQueryParamGuard],
            data: {
              ...TITLE_BAR_BASE,
              breadcrumbs: 'Purchase order detail',
              hideModuleTitle: true,
              module: 'financial-hub/purchase-orders'
            }
          }
        ]
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class FinancialHubRoutingModule {}
